/**
 * Editorial / sales-relationship badges for a company row — the classification
 * axis. Coverage customer, active campaign, prospect, and/or major watchlist.
 * Driven by `AnnouncementSummary.coverage_tiers` (computed backend-side from
 * entity_group_members). A company can carry several at once, so this renders
 * one badge per tier.
 *
 * There is deliberately NO 'picked' badge: a Discovery/triage selection is an
 * entity that belongs to none of these groups, so the *absence* of any badge is
 * itself the "picked / leftover" signal. The day-specific Picked filter pill is
 * the way to isolate those rows.
 *
 * Orthogonal to the Announcements page's provenance tabs (Today / Backdated /
 * Image Backfill), which are the temporal axis.
 */

// Each tier gets a distinct hue + an inset ring so the chip reads clearly
// against the row background (a flat grey fill on near-white doesn't stand out).
// Hierarchy by warmth: coverage (paid) green → campaign purple → prospect blue
// → major amber (broadest/lowest tier, but still coloured so it's legible).
const TIER_META: Record<string, { label: string; cls: string; title: string }> = {
  coverage: {
    label: 'Coverage',
    cls: 'bg-success-100 text-success-700 ring-1 ring-inset ring-success-300',
    title: 'Coverage customer (asx_coverage_customers — paid)',
  },
  campaign: {
    label: 'Campaign',
    cls: 'bg-purple-100 text-purple-700 ring-1 ring-inset ring-purple-300',
    title: 'In an active sales campaign',
  },
  prospect: {
    label: 'Prospect',
    cls: 'bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-300',
    title: 'Sales prospect (asx_coverage_prospects)',
  },
  major: {
    label: 'Major',
    cls: 'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-300',
    title: 'Broader monitored watchlist (asx_general_watchlist)',
  },
};

export function CoverageBadges({
  tiers,
  className = '',
}: {
  tiers?: string[] | null;
  className?: string;
}) {
  if (!tiers || tiers.length === 0) return null;
  return (
    <>
      {tiers.map((t) => {
        const m = TIER_META[t];
        if (!m) return null;
        return (
          <span
            key={t}
            title={m.title}
            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${m.cls} ${className}`}
          >
            {m.label}
          </span>
        );
      })}
    </>
  );
}
